import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/css/Styles1.css';
import './CRUD.css';
import './CompetenciasCRUD.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNotification } from '../components/NotificationSystem';
import SEO from '../components/SEO';
import { fetchV2 } from '../utils/apiV2';
import Icon from '../components/Icon';

const FUENTES = [
  { clave: 'auto', etiqueta: 'Auto' },
  { clave: 'jefe', etiqueta: 'Jefe' },
  { clave: 'par', etiqueta: 'Par' },
  { clave: 'colaborador', etiqueta: 'Colaborador' },
];

function comportamientoVacio() {
  return { codigo: '', descripcion: '', fuentes: { auto: true, jefe: true, par: true, colaborador: true } };
}

function formularioVacio() {
  return {
    codigo: '',
    nombre: '',
    definicion: '',
    comportamientos: [comportamientoVacio(), comportamientoVacio(), comportamientoVacio()],
  };
}

function CompetenciasCRUD({ onLogout }) {
  const { success, error: showError } = useNotification();
  const navigate = useNavigate();

  const [niveles, setNiveles] = useState([]);
  const [codigoBancoSeleccionado, setCodigoBancoSeleccionado] = useState('');
  const [banco, setBanco] = useState(null);
  const [loadingNiveles, setLoadingNiveles] = useState(true);
  const [loadingBanco, setLoadingBanco] = useState(false);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [idCompetenciaEditando, setIdCompetenciaEditando] = useState(null);
  const [formulario, setFormulario] = useState(formularioVacio());
  const [guardando, setGuardando] = useState(false);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
  const esBancoExentoLimites = codigoBancoSeleccionado === 'OP-CAMPO';

  // Catálogo de niveles/bancos: mismo endpoint público que ya usa Gestión de
  // Cargos (sin token) — es un catálogo de referencia, no un dato sensible.
  useEffect(() => {
    const fetchNiveles = async () => {
      try {
        setLoadingNiveles(true);
        const response = await fetch(`${apiUrl}/api/cargos/niveles`);
        const result = await response.json();
        if (result.success) {
          setNiveles(result.data);
          if (result.data.length > 0) {
            setCodigoBancoSeleccionado(result.data[0].codigo);
          }
        } else {
          throw new Error(result.message || 'Error al cargar niveles');
        }
      } catch (e) {
        setError(e.message || 'Error al cargar niveles');
      } finally {
        setLoadingNiveles(false);
      }
    };
    fetchNiveles();
  }, [apiUrl]);

  const fetchBanco = useCallback(async () => {
    if (!codigoBancoSeleccionado) return;
    try {
      setLoadingBanco(true);
      setError(null);
      const { ok, status, data } = await fetchV2(`api/v2/bancos/${codigoBancoSeleccionado}/vigente`);
      if (status === 401) {
        throw new Error('Sesión sin token válido para el módulo de configuración (api/v2). Vuelva a iniciar sesión.');
      }
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al cargar el banco de competencias');
      }
      setBanco(data.data);
    } catch (e) {
      setError(e.message || 'Error al cargar el banco de competencias');
      setBanco(null);
    } finally {
      setLoadingBanco(false);
    }
  }, [codigoBancoSeleccionado]);

  useEffect(() => {
    fetchBanco();
  }, [fetchBanco]);

  const handleBackClick = () => navigate('/admin');

  const handleAddClick = () => {
    setIsEditing(false);
    setIdCompetenciaEditando(null);
    setFormulario(formularioVacio());
    setShowForm(true);
  };

  const handleEditClick = (competencia) => {
    setIsEditing(true);
    setIdCompetenciaEditando(competencia.id_competencia_cat);
    setFormulario({
      codigo: competencia.codigo,
      nombre: competencia.nombre,
      definicion: competencia.definicion || '',
      comportamientos: (competencia.comportamientos || []).map((c) => ({
        codigo: c.codigo,
        descripcion: c.descripcion,
        fuentes: { ...c.fuentes },
      })),
    });
    setShowForm(true);
  };

  const handleCancelForm = () => setShowForm(false);

  const handleCampoChange = (e) => {
    const { name, value } = e.target;
    setFormulario((prev) => ({ ...prev, [name]: value }));
  };

  const handleComportamientoChange = (index, campo, value) => {
    setFormulario((prev) => {
      const comportamientos = [...prev.comportamientos];
      comportamientos[index] = { ...comportamientos[index], [campo]: value };
      return { ...prev, comportamientos };
    });
  };

  const handleFuenteToggle = (index, fuente) => {
    setFormulario((prev) => {
      const comportamientos = [...prev.comportamientos];
      comportamientos[index] = {
        ...comportamientos[index],
        fuentes: { ...comportamientos[index].fuentes, [fuente]: !comportamientos[index].fuentes[fuente] },
      };
      return { ...prev, comportamientos };
    });
  };

  const handleAgregarComportamiento = () => {
    setFormulario((prev) => ({ ...prev, comportamientos: [...prev.comportamientos, comportamientoVacio()] }));
  };

  const handleQuitarComportamiento = (index) => {
    setFormulario((prev) => ({
      ...prev,
      comportamientos: prev.comportamientos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = {
        codigo: formulario.codigo.trim(),
        nombre: formulario.nombre.trim(),
        definicion: formulario.definicion.trim() || null,
        comportamientos: formulario.comportamientos.map((c) => ({
          codigo: c.codigo.trim(),
          descripcion: c.descripcion.trim(),
          fuentes: c.fuentes,
        })),
      };

      const { ok, data } = isEditing
        ? await fetchV2(`api/v2/competencias/${idCompetenciaEditando}`, { method: 'PUT', body: JSON.stringify(cuerpo) })
        : await fetchV2(`api/v2/bancos/${codigoBancoSeleccionado}/competencias`, { method: 'POST', body: JSON.stringify(cuerpo) });

      if (!ok || !data?.success) {
        throw new Error(data?.message || `Error al ${isEditing ? 'editar' : 'crear'} la competencia`);
      }

      setShowForm(false);
      success(
        isEditing ? 'Competencia editada' : 'Competencia creada',
        isEditing
          ? 'Se creó una nueva versión; la anterior quedó inactivada y sigue disponible para evaluaciones históricas.'
          : 'Competencia creada con éxito'
      );
      fetchBanco();
    } catch (e) {
      showError('Error de operación', e.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleInactivar = async (competencia) => {
    if (!window.confirm(`¿Inactivar la competencia "${competencia.nombre}"? Dejará de usarse en evaluaciones nuevas, pero permanece disponible para las históricas.`)) {
      return;
    }
    try {
      const { ok, data } = await fetchV2(`api/v2/competencias/${competencia.id_competencia_cat}/inactivar`, { method: 'POST' });
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al inactivar la competencia');
      }
      success('Competencia inactivada', 'La competencia dejó de estar vigente para nuevas evaluaciones');
      fetchBanco();
    } catch (e) {
      showError('Error al inactivar', e.message);
    }
  };

  const handleToggleFuenteExistente = async (comportamiento, fuente) => {
    const nuevoValor = !comportamiento.fuentes[fuente];
    try {
      const { ok, data } = await fetchV2(`api/v2/comportamientos/${comportamiento.id_comportamiento}/fuentes`, {
        method: 'PUT',
        body: JSON.stringify({ [fuente]: nuevoValor }),
      });
      if (!ok || !data?.success) {
        throw new Error(data?.message || 'Error al actualizar la fuente');
      }
      fetchBanco();
    } catch (e) {
      showError('Error al actualizar fuente', e.message);
    }
  };

  return (
    <>
      <SEO
        title="Banco de Competencias - Panel de Administración"
        description="Configura el banco de competencias, comportamientos observables y fuentes de evaluación por nivel organizacional en Meridian Consulting LTDA."
        keywords="banco de competencias, comportamientos observables, fuentes de evaluación, niveles organizacionales, Meridian Consulting"
        url="https://evaluacion.meridianltda.com/admin/competencias"
      />
      <div className="crud-container competencias-crud">
        <Header onLogout={onLogout} />

        <main className="crud-main">
          <div className="crud-header">
            <button className="back-button" onClick={handleBackClick}>
              Volver al Panel
            </button>
            <div className="crud-header-titles">
              <h1>Banco de Competencias</h1>
              <p className="crud-header-subtitle">Configura competencias, comportamientos y fuentes por nivel organizacional</p>
            </div>
            <button className="add-button" onClick={handleAddClick} disabled={!codigoBancoSeleccionado}>
              Agregar Competencia
            </button>
          </div>

          <div className="nivel-selector">
            <label htmlFor="nivel-select">Nivel organizacional / tipo de evaluación:</label>
            {loadingNiveles ? (
              <span className="nivel-selector-loading">Cargando niveles...</span>
            ) : (
              <select
                id="nivel-select"
                value={codigoBancoSeleccionado}
                onChange={(e) => setCodigoBancoSeleccionado(e.target.value)}
              >
                {niveles.map((n) => (
                  <option key={n.id_banco} value={n.codigo}>
                    {n.nombre} ({n.codigo})
                  </option>
                ))}
              </select>
            )}
          </div>

          {esBancoExentoLimites && (
            <p className="nivel-aviso">
              Este nivel (Operación/Campo) está exento del límite administrativo de 5 competencias / 3 comportamientos cada una.
            </p>
          )}

          {error && (
            <div className="error-banner">
              <span>{error}</span>
              <button onClick={fetchBanco}>Reintentar</button>
            </div>
          )}

          {showForm && (
            <div className="crud-form-container">
              <form className="crud-form competencia-form" onSubmit={handleSubmit}>
                <h2>{isEditing ? 'Editar Competencia (crea nueva versión)' : 'Nueva Competencia'}</h2>

                <div className="form-body">
                  <div className="form-group">
                    <label htmlFor="codigo">Código:</label>
                    <input type="text" id="codigo" name="codigo" value={formulario.codigo} onChange={handleCampoChange} required placeholder="EST-C06" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="nombre">Nombre:</label>
                    <input type="text" id="nombre" name="nombre" value={formulario.nombre} onChange={handleCampoChange} required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="definicion">Definición:</label>
                    <textarea id="definicion" name="definicion" value={formulario.definicion} onChange={handleCampoChange} rows="3" />
                  </div>

                  <div className="comportamientos-section">
                    <div className="comportamientos-header">
                      <label>Comportamientos observables:</label>
                      {esBancoExentoLimites && (
                        <button type="button" className="add-comportamiento-button" onClick={handleAgregarComportamiento}>
                          + Agregar comportamiento
                        </button>
                      )}
                    </div>

                    {formulario.comportamientos.map((c, index) => (
                      <div className="comportamiento-card" key={index}>
                        <div className="comportamiento-card-header">
                          <span>Comportamiento {index + 1}</span>
                          {esBancoExentoLimites && formulario.comportamientos.length > 1 && (
                            <button type="button" className="remove-comportamiento-button" onClick={() => handleQuitarComportamiento(index)}>
                              Quitar
                            </button>
                          )}
                        </div>
                        <div className="form-group">
                          <input
                            type="text"
                            placeholder="Código (ej: EST-C06-01)"
                            value={c.codigo}
                            onChange={(e) => handleComportamientoChange(index, 'codigo', e.target.value)}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <textarea
                            placeholder="Descripción del comportamiento observable"
                            value={c.descripcion}
                            onChange={(e) => handleComportamientoChange(index, 'descripcion', e.target.value)}
                            rows="2"
                            required
                          />
                        </div>
                        <div className="fuentes-toggle-group">
                          {FUENTES.map((f) => (
                            <label key={f.clave} className="fuente-checkbox">
                              <input
                                type="checkbox"
                                checked={!!c.fuentes[f.clave]}
                                onChange={() => handleFuenteToggle(index, f.clave)}
                              />
                              {f.etiqueta}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-buttons">
                  <button type="button" className="cancel-button" onClick={handleCancelForm}>
                    Cancelar
                  </button>
                  <button type="submit" className="save-button" disabled={guardando}>
                    {guardando ? 'Guardando...' : isEditing ? 'Guardar nueva versión' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {loadingBanco ? (
            <div className="loading">Cargando competencias...</div>
          ) : banco && (banco.competencias || []).length > 0 ? (
            <div className="competencias-grid">
              {banco.competencias.map((competencia) => (
                <div className="competencia-card" key={competencia.id_competencia_cat}>
                  <div className="competencia-card-header">
                    <div>
                      <span className="competencia-codigo">{competencia.codigo}</span>
                      <h3>{competencia.nombre}</h3>
                    </div>
                    <div className="competencia-acciones">
                      <button className="edit-button" onClick={() => handleEditClick(competencia)}>
                        <Icon name="edit" size={14} /> Editar
                      </button>
                      <button className="delete-button" onClick={() => handleInactivar(competencia)}>
                        <Icon name="ban" size={14} /> Inactivar
                      </button>
                    </div>
                  </div>
                  {competencia.definicion && <p className="competencia-definicion">{competencia.definicion}</p>}

                  <ul className="comportamientos-list">
                    {(competencia.comportamientos || []).map((c) => (
                      <li key={c.id_comportamiento} className="comportamiento-item">
                        <div className="comportamiento-texto">
                          <span className="comportamiento-codigo">{c.codigo}</span>
                          <span>{c.descripcion}</span>
                        </div>
                        <div className="fuentes-toggle-group fuentes-toggle-group-compact">
                          {FUENTES.map((f) => (
                            <label key={f.clave} className={`fuente-chip ${c.fuentes[f.clave] ? 'fuente-chip-on' : 'fuente-chip-off'}`}>
                              <input
                                type="checkbox"
                                checked={!!c.fuentes[f.clave]}
                                onChange={() => handleToggleFuenteExistente(c, f.clave)}
                              />
                              {f.etiqueta}
                            </label>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            !error && <p className="no-data">Este nivel todavía no tiene competencias activas registradas.</p>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}

export default CompetenciasCRUD;
